import { useState, useEffect, useMemo } from 'react';
import { Personnel } from '../../../../shared/types/game';
import { PersonnelConfig, Competence } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import { statuts } from '../../../../config';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import {
  normalizePlanningGroupeValue,
  type PlanningGroupeKey,
} from '../OperationalPlanningModal';
import { loadPersonnelDetails } from './loadPersonnelDetails';

export interface UsePersonnelTabProps {
  personnel: Personnel[];
  buildingType: string;
  buildingId: number;
  onPersonnelAdded?: () => void;
}

export function usePersonnelTab({
  personnel,
  buildingType,
  buildingId,
  onPersonnelAdded,
}: UsePersonnelTabProps) {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  // UI modals
  const [showOperationalPlanningModal, setShowOperationalPlanningModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Recruit form
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecruiting, setIsRecruiting] = useState(false);
  const [recruitError, setRecruitError] = useState<string | null>(null);
  const [recruitSuccess, setRecruitSuccess] = useState<string | null>(null);

  // List filters / pagination / view
  const [personnelSearchQuery, setPersonnelSearchQuery] = useState('');
  const [personnelFilterActif, setPersonnelFilterActif] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');

  // Personnel details modal
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);
  const [showPersonnelDetailsModal, setShowPersonnelDetailsModal] = useState(false);
  const [personnelCompetences, setPersonnelCompetences] = useState<Competence[]>([]);
  const [gradeDefaultCompetences, setGradeDefaultCompetences] = useState<Competence[]>([]);
  const [loadingCompetences, setLoadingCompetences] = useState(false);
  const [competenceSearchQuery, setCompetenceSearchQuery] = useState('');
  const [posteNom, setPosteNom] = useState<string | null>(null);
  const [statutGeneral, setStatutGeneral] = useState<string | null>(null);
  const [statutPlanning, setStatutPlanning] = useState<string | null>(null);
  const [derniereIntervention, setDerniereIntervention] = useState<{ date: string; heure: string } | null>(null);
  const [personnelGroupeSelect, setPersonnelGroupeSelect] = useState<'' | PlanningGroupeKey>('');
  const [savingGroupe, setSavingGroupe] = useState(false);
  const [editingNom, setEditingNom] = useState(false);
  const [nomValue, setNomValue] = useState('');
  const [prenomValue, setPrenomValue] = useState('');
  const [savingNom, setSavingNom] = useState(false);
  const [deletingPersonnelId, setDeletingPersonnelId] = useState<number | null>(null);

  const { t } = usePageTranslation('game', 'Map');

  const serviceId = getServiceIdFromBuildingType(buildingType);

  // ── Filtered & paginated personnel ──────────────────────────────────────
  const filteredPersonnel = useMemo(() => {
    return personnel.filter((p) => {
      const searchLower = personnelSearchQuery.toLowerCase().trim();
      if (
        searchLower &&
        !(
          p.nom.toLowerCase().includes(searchLower) ||
          p.prenom.toLowerCase().includes(searchLower) ||
          p.grade.toLowerCase().includes(searchLower)
        )
      ) {
        return false;
      }
      if (personnelFilterActif === 'actif' && !p.actif) return false;
      if (personnelFilterActif === 'inactif' && p.actif) return false;
      return true;
    });
  }, [personnel, personnelSearchQuery, personnelFilterActif]);

  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);

  const paginatedPersonnel = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPersonnel.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPersonnel, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [personnelSearchQuery, personnelFilterActif]);

  // ── Statut helpers ───────────────────────────────────────────────────────
  const getStatutGeneralColors = (statutNom: string | null) => {
    if (!statutNom) return { backgroundColor: '#6b7280', textColor: '#fff' };
    const statut = statuts.statuts.find(
      (s) => s.nom_statut === statutNom.toUpperCase()
    );
    return statut?.couleurs || { backgroundColor: '#6b7280', textColor: '#fff' };
  };

  const getStatutPlanningColors = (statutNom: string | null) => {
    if (!statutNom) return { backgroundColor: '#6b7280', textColor: '#fff' };
    const statut = statuts.statuts_planning.find(
      (s) => s.nom.toUpperCase() === statutNom.toUpperCase()
    );
    return statut?.couleurs || { backgroundColor: '#6b7280', textColor: '#fff' };
  };

  // ── Personnel details modal opener ───────────────────────────────────────
  const handleOpenPersonnelDetails = async (p: Personnel) => {
    setSelectedPersonnel(p);
    setNomValue(p.nom);
    setPrenomValue(p.prenom);
    setEditingNom(false);
    setShowPersonnelDetailsModal(true);
    setLoadingCompetences(true);
    setPersonnelCompetences([]);
    setGradeDefaultCompetences([]);
    setCompetenceSearchQuery('');
    setPosteNom(null);
    setStatutGeneral(null);
    setStatutPlanning(null);
    setDerniereIntervention(null);
    setPersonnelGroupeSelect(normalizePlanningGroupeValue(p.groupe) ?? '');
    setSavingGroupe(false);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingCompetences(false);
      return;
    }

    try {
      const details = await loadPersonnelDetails(p, buildingId, token);
      setPersonnelCompetences(details.personnelCompetences);
      setGradeDefaultCompetences(details.gradeDefaultCompetences);
      setPosteNom(details.posteNom);
      setStatutGeneral(details.statutGeneral);
      setStatutPlanning(details.statutPlanning);
      setDerniereIntervention(details.derniereIntervention);
      if (details.groupe !== undefined) {
        setPersonnelGroupeSelect(
          normalizePlanningGroupeValue(details.groupe as string | null | undefined) ?? ''
        );
      }
    } finally {
      setLoadingCompetences(false);
    }
  };

  // ── Save name ────────────────────────────────────────────────────────────
  const handleSaveNom = async () => {
    if (!selectedPersonnel || !nomValue.trim() || !prenomValue.trim()) return;
    if (
      nomValue.trim() === selectedPersonnel.nom &&
      prenomValue.trim() === selectedPersonnel.prenom
    ) {
      setEditingNom(false);
      return;
    }

    setSavingNom(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `${getApiBaseUrl(true)}/buildings/personnel/${selectedPersonnel.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ nom: nomValue.trim(), prenom: prenomValue.trim() }),
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setSelectedPersonnel({ ...selectedPersonnel, nom: nomValue.trim(), prenom: prenomValue.trim() });
        setEditingNom(false);
        onPersonnelAdded?.();
      } else {
        alert(data.error || 'Erreur lors de la sauvegarde du nom');
      }
    } catch {
      alert('Erreur lors de la sauvegarde du nom');
    } finally {
      setSavingNom(false);
    }
  };

  const handleCancelEditNom = () => {
    if (selectedPersonnel) {
      setNomValue(selectedPersonnel.nom);
      setPrenomValue(selectedPersonnel.prenom);
    }
    setEditingNom(false);
  };

  // ── Delete personnel ─────────────────────────────────────────────────────
  const handleDeletePersonnel = async (personnelItem: Personnel) => {
    if (deletingPersonnelId !== null) return;
    const confirmed = window.confirm(
      `Supprimer ${personnelItem.prenom} ${personnelItem.nom} du bâtiment ?`
    );
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Session expirée. Reconnectez-vous.');
      return;
    }
    setDeletingPersonnelId(personnelItem.id);
    try {
      const response = await fetch(
        `${getApiBaseUrl(true)}/buildings/personnel/${personnelItem.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !(data as any).success) {
        alert((data as { error?: string }).error || 'Suppression impossible.');
        return;
      }
      if (selectedPersonnel?.id === personnelItem.id) {
        setShowPersonnelDetailsModal(false);
        setSelectedPersonnel(null);
      }
      onPersonnelAdded?.();
    } catch {
      alert('Erreur réseau lors de la suppression.');
    } finally {
      setDeletingPersonnelId(null);
    }
  };

  // ── Save groupe ──────────────────────────────────────────────────────────
  const savePersonnelGroupe = async (value: '' | PlanningGroupeKey) => {
    if (!selectedPersonnel || savingGroupe) return;
    const prev = normalizePlanningGroupeValue(selectedPersonnel.groupe) ?? '';
    if (value === prev) return;

    setPersonnelGroupeSelect(value);
    setSavingGroupe(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setPersonnelGroupeSelect(prev);
      setSavingGroupe(false);
      return;
    }

    try {
      const response = await fetch(
        `${getApiBaseUrl(true)}/buildings/personnel/${selectedPersonnel.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            nom: selectedPersonnel.nom,
            prenom: selectedPersonnel.prenom,
            groupe: value === '' ? null : value,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.ok && (data as any).success && (data as any).data) {
        const updated = (data as any).data as Personnel;
        setSelectedPersonnel({
          ...selectedPersonnel,
          groupe: updated.groupe ?? (value === '' ? null : value),
        });
        onPersonnelAdded?.();
      } else {
        setPersonnelGroupeSelect(prev);
        alert(
          (data as { error?: string }).error ||
            t('operationalPlanning.personnelDetailGroupeSaveError')
        );
      }
    } catch {
      setPersonnelGroupeSelect(prev);
      alert(t('operationalPlanning.personnelDetailGroupeSaveError'));
    } finally {
      setSavingGroupe(false);
    }
  };

  // ── Recruit ──────────────────────────────────────────────────────────────
  const loadPersonnelConfigs = async () => {
    if (!serviceId) return;
    setLoadingConfigs(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${getApiBaseUrl(true)}/config/personnel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setPersonnelConfigs(
            data.data.filter((config: PersonnelConfig) => config.id_service === serviceId)
          );
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des configurations de personnel:', error);
    } finally {
      setLoadingConfigs(false);
    }
  };

  useEffect(() => {
    if (showAddModal) {
      loadPersonnelConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal, serviceId]);

  const handleRecruit = async (config: PersonnelConfig, quantity: number = 1) => {
    if (!currentUser || isRecruiting) return;
    setIsRecruiting(true);
    setRecruitError(null);
    setRecruitSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRecruitError('Non authentifié');
        return;
      }
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/recruit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ configId: config.id, batimentId: buildingId, quantity }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setRecruitError(data.error || 'Erreur lors du recrutement');
        return;
      }
      if (data.data?.newMoney !== undefined && currentUser) {
        dispatch(updateUser({ argents: data.data.newMoney }));
      }
      const count = data.data?.count ?? quantity;
      setRecruitSuccess(data.message || `${count} personnel${count > 1 ? 's' : ''} recruté${count > 1 ? 's' : ''} avec succès`);
      onPersonnelAdded?.();
      // Signal global (écouté notamment par le tutoriel).
      window.dispatchEvent(new CustomEvent('opsecours:staff-recruited'));
      setTimeout(() => {
        setRecruitSuccess(null);
      }, 2000);
    } catch (error: any) {
      console.error('Erreur lors du recrutement:', error);
      setRecruitError('Erreur réseau lors du recrutement');
    } finally {
      setIsRecruiting(false);
    }
  };

  return {
    // modal visibility
    showOperationalPlanningModal,
    setShowOperationalPlanningModal,
    showAddModal,
    setShowAddModal,

    // recruit
    personnelConfigs,
    loadingConfigs,
    searchQuery,
    setSearchQuery,
    isRecruiting,
    recruitError,
    recruitSuccess,
    handleRecruit,

    // list
    filteredPersonnel,
    paginatedPersonnel,
    totalPages,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    personnelSearchQuery,
    setPersonnelSearchQuery,
    personnelFilterActif,
    setPersonnelFilterActif,
    viewMode,
    setViewMode,

    // personnel details
    selectedPersonnel,
    setSelectedPersonnel,
    showPersonnelDetailsModal,
    setShowPersonnelDetailsModal,
    personnelCompetences,
    gradeDefaultCompetences,
    loadingCompetences,
    competenceSearchQuery,
    setCompetenceSearchQuery,
    posteNom,
    statutGeneral,
    statutPlanning,
    derniereIntervention,
    personnelGroupeSelect,
    savingGroupe,
    editingNom,
    setEditingNom,
    nomValue,
    setNomValue,
    prenomValue,
    setPrenomValue,
    savingNom,
    deletingPersonnelId,

    // handlers
    handleOpenPersonnelDetails,
    handleSaveNom,
    handleCancelEditNom,
    handleDeletePersonnel,
    savePersonnelGroupe,

    // helpers
    getStatutGeneralColors,
    getStatutPlanningColors,
    t,
  };
}
